import { styled } from '@stitches/configed';
import { message, Select } from 'antd';
import { useState } from 'react';
import { restaurantService } from '../service';
import { useAppSelector } from '../../../store';


const StatusButton = styled('div', {
  backgroundColor: "#E8EAED",
  width: '100%',
  height: 'auto',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 6,
  fontWeight: 500,
  fontSize: 16,
  letterSpacing: 0,
  padding: 12,
  '> p': {
    margin: 0
  },
  variants: {
    status: {
      opening: {
        backgroundColor: '#17AFA7',
        color: '#fff',
      },
      temporaryUnavailable: {
        backgroundColor: '#E8EAED',
        color: '#021337',
        '> p': {
          fontWeight: 500,
          fontSize: 16,
          lineHeight: 1,
          letterSpacing: 0,
          color: '#021337'
        },
        '> span': {
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 1.6,
          letterSpacing: 0,
          color: '#4E5A73'
        }
      },
      closed: {
        backgroundColor: '#FAEDE9',
        color: '#d33c0c',
        '> p': {
          margin: 0,
          fontWeight: 500,
          fontSize: 16,
          lineHeight: 1,
          letterSpacing: 0,
          color: '#D33C0C'
        },
        '> span': {
          fontWeight: 400,
          fontSize: 12,
          lineHeight: 1.6,
          letterSpacing: 0,
          color: '#D33C0C'
        }
      },
    }
  }
});

const DropdownDecoration = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  padding: '12px 24px',

  '> p': {
    fontWeight: 400,
    fontSize: 24,
    letterSpacing: 0,
    textAlign: 'center',
    color: '#021337',
  }
});

export enum ForceChangeRestaurantState {
  FORCE_OPEN_HOURS_OF_OPERATION = 'force_open_hours_of_operation',
  FORCE_CLOSE_HOURS_OF_OPERATION = 'force_close_hours_of_operation',
  FORCE_PAUSE_HOURS_OF_OPERATION = 'force_pause_hours_of_operation',
}

const selectStyles = {
  [ForceChangeRestaurantState.FORCE_OPEN_HOURS_OF_OPERATION]: {
    background: '#E7F7F0 !important',
    color: '#0D9A62'
  },
  [ForceChangeRestaurantState.FORCE_PAUSE_HOURS_OF_OPERATION]: {
    background: '#E8EAED !important',
    color: '#021337'
  },
  [ForceChangeRestaurantState.FORCE_CLOSE_HOURS_OF_OPERATION]: {
    background: '#FAEDE9 !important',
    color: '#D33C0C'
  }
};

const RestaurantStatuses = () => {
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const [selectActiveStyle, setSelectActiveStyle] = useState<{ background: string; color: string } | undefined>(undefined);
  const [selected, setSelected] = useState<ForceChangeRestaurantState>(ForceChangeRestaurantState.FORCE_CLOSE_HOURS_OF_OPERATION);

  const [statuses, setStatuses] = useState([
    {
      key: 'active',
      title: 'Đang nhận đơn',
      value: ForceChangeRestaurantState.FORCE_OPEN_HOURS_OF_OPERATION,
    },
    {
      key: 'pause',
      title: 'Tạm dừng nhận đơn',
      note: '(Tự nhận đơn lại sau 1 giờ)',
      value: ForceChangeRestaurantState.FORCE_PAUSE_HOURS_OF_OPERATION,
    },
    {
      key: 'inactive',
      title: 'Đóng cửa',
      note: '(Tự mở lại theo giờ mở cửa tiếp theo)',
      value: ForceChangeRestaurantState.FORCE_CLOSE_HOURS_OF_OPERATION,
    }
  ]);

  const CustomSelect = styled(Select, {
    '.ant-select-selector': {
      border: 'none !important',
      ...(selectActiveStyle || selectStyles[ForceChangeRestaurantState.FORCE_CLOSE_HOURS_OF_OPERATION])
    }
  });

  const handleStatusChange = (value: any) => {
    setSelectActiveStyle(selectStyles[value as ForceChangeRestaurantState]);
    setSelected(value);

    if (!currentRestaurant?.id) return;
    restaurantService.changeRestaurantStatus(currentRestaurant?.id, { state: value }).then((res) => {
      if (res.data && res.data.message === 'success') {
        message.success('Thành công');
      }
    });
  };

  return (
    <CustomSelect
      className='restaurant-statuses'
      style={{ minWidth: 'max-content' }}
      dropdownMatchSelectWidth={400}
      placement='bottomRight'
      value={selected}
      optionLabelProp="label"
      popupClassName="restaurant-statuses__popup"
      onChange={handleStatusChange}
    >
      <Select.Option key="none" disabled style={{ display: 'flex' }}>
        <DropdownDecoration>
          <svg width="46" height="30" viewBox="0 0 46 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.6666 4.98747C11.9039 4.98747 9.25439 6.08493 7.30089 8.03844C5.34739 9.99194 4.24992 12.6415 4.24992 15.4041C4.24992 18.1668 5.34739 20.8163 7.30089 22.7698C9.25439 24.7233 11.9039 25.8208 14.6666 25.8208H31.3333C34.0959 25.8208 36.7454 24.7233 38.699 22.7698C40.6525 20.8163 41.7499 18.1668 41.7499 15.4041C41.7499 12.6415 40.6525 9.99194 38.699 8.03844C36.7454 6.08493 34.0959 4.98747 31.3333 4.98747H14.6666ZM14.6666 0.820801H31.3333C35.201 0.820801 38.9103 2.35725 41.6452 5.09216C44.3801 7.82707 45.9166 11.5364 45.9166 15.4041C45.9166 19.2719 44.3801 22.9812 41.6452 25.7161C38.9103 28.451 35.201 29.9875 31.3333 29.9875H14.6666C10.7988 29.9875 7.08952 28.451 4.35461 25.7161C1.61971 22.9812 0.083252 19.2719 0.083252 15.4041C0.083252 11.5364 1.61971 7.82707 4.35461 5.09216C7.08952 2.35725 10.7988 0.820801 14.6666 0.820801ZM14.6666 21.6541C13.009 21.6541 11.4193 20.9957 10.2472 19.8236C9.07507 18.6514 8.41659 17.0617 8.41659 15.4041C8.41659 13.7465 9.07507 12.1568 10.2472 10.9847C11.4193 9.81261 13.009 9.15413 14.6666 9.15413C16.3242 9.15413 17.9139 9.81261 19.086 10.9847C20.2581 12.1568 20.9166 13.7465 20.9166 15.4041C20.9166 17.0617 20.2581 18.6514 19.086 19.8236C17.9139 20.9957 16.3242 21.6541 14.6666 21.6541Z" fill="#021337" />
          </svg>

          <p>Thay đổi trạng thái nhận đơn</p>
        </DropdownDecoration>
      </Select.Option>

      {statuses.map((status) => (
        <Select.Option key={status.key} value={status.value} label={status.title}>
          <StatusButton status={status.key as any} >
            {status.title && <p>{status.title}</p>}
            {status.note && <span>{status.note}</span>}
          </StatusButton>
        </Select.Option>
      ))}
    </CustomSelect>
  );
};

export default RestaurantStatuses;
